import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Gamepad2,
  Bluetooth,
  Loader2,
  RefreshCw,
  Check,
} from "lucide-react";
import clsx from "clsx";
import { Api } from "../lib/api.js";

/**
 * Pair a G20S Bluetooth remote with a chosen Pi (2026-10-28).
 *
 * The Pi is headless — screensaver + playback only, no desktop pairing UI —
 * and one G20S bonds to exactly ONE Pi by hardware spec, so the flow starts
 * by picking the Pi. The cloud proxies /pair-remote/* to that Pi through its
 * reverse tunnel; once the OS bond exists, the Pi's playback app hears the
 * remote within ~5 s (its /dev/input watcher rescans on a loop — no restart).
 */

type Banner = { kind: "ok" | "err" | "info"; node: ReactNode } | null;

const INPUT_CLS =
  "w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none";

function BannerBox({ banner }: { banner: Banner }): JSX.Element | null {
  if (!banner) return null;
  return (
    <div
      className={clsx(
        "rounded-md border px-3 py-2.5 text-sm leading-relaxed",
        banner.kind === "ok" && "bg-jade/15 border-jade/50 text-emerald-300",
        banner.kind === "err" && "bg-vermilion/15 border-vermilion/50 text-vermilionHi",
        banner.kind === "info" && "bg-gold/10 border-gold/40 text-goldHi",
      )}
    >
      {banner.node}
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 bg-ink/60 border border-hairline rounded-md px-3 py-2">
      <span className="section-label text-[10px]">{label}</span>
      <span className="font-mono text-sm text-goldHi text-right">{value}</span>
    </div>
  );
}

/** Clone remotes name themselves loosely — G20S, Dupad story, air mouse. */
const remoteish = (n: string | null): boolean => !!n && /g20|dupad|remote|mouse/i.test(n);

export function RemotePair(): JSX.Element {
  const [banner, setBanner] = useState<Banner>(null);

  // ── Which Pi? Same selector as the Wi-Fi page — one remote, one Pi. ─────
  const devicesQ = useQuery({
    queryKey: ["cast-devices"],
    queryFn: Api.cast.devices,
    refetchInterval: 15_000,
    retry: false,
  });
  const pis = devicesQ.data?.devices ?? [];
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const deviceId = picked ?? pis[0]?.device_id;
  const selectedLabel = pis.find((p) => p.device_id === deviceId)?.label ?? null;

  // ── Paired remotes on the selected Pi ───────────────────────────────────
  const statusQ = useQuery({
    queryKey: ["remote-status", deviceId],
    queryFn: () => Api.remote.status(deviceId),
    enabled: !!deviceId,
    refetchInterval: 10_000,
    retry: false,
  });
  const remotes = statusQ.data?.remotes ?? [];
  const inputNodes = statusQ.data?.input_nodes ?? [];
  const inputLive = inputNodes.some((n) => remoteish(n));

  // ── Add-a-remote scan ───────────────────────────────────────────────────
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<{ mac: string; name: string | null }[]>([]);
  const [pairing, setPairing] = useState<string | null>(null);

  // Switching Pis mid-scan makes the discovered list meaningless — reset.
  useEffect(() => {
    setScanning(false);
    setFound([]);
    setBanner(null);
  }, [deviceId]);

  async function scan(): Promise<void> {
    setFound([]);
    setBanner({
      kind: "info",
      node: "Scanning ~25 s — hold OK + Vol− on the remote until its LED flashes, then pick it below.",
    });
    try {
      const d = await Api.remote.scanStart(deviceId);
      if (!d.ok) throw new Error(d.error ?? "scan failed");
      setScanning(true);
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    }
  }

  useEffect(() => {
    if (!scanning) return;
    let elapsed = 0;
    const t = window.setInterval(() => {
      elapsed += 3;
      void Api.remote.devices(deviceId)
        .then((d) => setFound(d.devices ?? []))
        .catch(() => undefined);
      if (elapsed >= 27) setScanning(false);
    }, 3_000);
    return () => window.clearInterval(t);
  }, [scanning, deviceId]);

  async function pair(mac: string, name: string): Promise<void> {
    if (pairing) return;
    setPairing(mac);
    setBanner({ kind: "info", node: `Pairing with ${name} (${mac})…` });
    try {
      const d = await Api.remote.pair(deviceId, { mac, name });
      if (!d.ok) throw new Error(d.error ?? "pair failed");
      setBanner({
        kind: "ok",
        node: "✓ Paired and trusted — the remote reconnects by itself after it sleeps. Its buttons are live within ~5 s.",
      });
      setScanning(false);
      void statusQ.refetch();
    } catch (e) {
      setBanner({ kind: "err", node: `✗ ${(e as Error).message}` });
    } finally {
      setPairing(null);
    }
  }

  async function forget(mac: string): Promise<void> {
    if (!window.confirm("Forget this remote? It will need pairing again.")) return;
    try {
      await Api.remote.forget(deviceId, { mac });
    } catch {
      /* best effort */
    }
    void statusQ.refetch();
  }

  // Remote-looking devices float to the top of the scan list.
  const sortedFound = [...found].sort((a, b) => Number(remoteish(b.name)) - Number(remoteish(a.name)));

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-xl">
      <h1 className="font-display text-3xl text-ivory flex items-center gap-3">
        <Gamepad2 size={24} className="text-gold" /> Pair Remote
        {selectedLabel && (
          <span className="text-base text-parchment/70">· {selectedLabel}</span>
        )}
      </h1>
      <div className="ornament-rule" />

      {pis.length === 0 ? (
        <div className="card p-4 text-sm text-parchment">
          No playback devices have registered yet — a Pi announces itself within ~30 s of boot.
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <label className="block text-xs text-parchment mb-1" htmlFor="remote-pi">
              Playback device
            </label>
            <select
              id="remote-pi"
              className={INPUT_CLS}
              value={deviceId ?? ""}
              onChange={(e) => setPicked(e.target.value || undefined)}
            >
              {pis.map((p) => (
                <option key={p.device_id} value={p.device_id}>
                  {p.label} — {p.online ? "online" : "offline"}
                </option>
              ))}
            </select>
          </div>

          {/* Paired remotes */}
          <section className="space-y-2">
            <div className="section-label">Remotes on this Pi</div>
            <div className="card p-4 space-y-3">
              {remotes.length === 0 ? (
                <Row label="Remotes" value="none paired" />
              ) : (
                remotes.map((r) => (
                  <div
                    key={r.mac}
                    className="flex items-center justify-between gap-4 bg-ink/60 border border-hairline rounded-md px-3 py-2"
                  >
                    <span className="section-label text-[10px] flex items-center gap-1.5">
                      <Gamepad2 size={12} /> {r.name}
                    </span>
                    <span className="font-mono text-sm text-goldHi flex items-center gap-2">
                      {r.connected ? "connected" : r.paired ? "paired" : "not paired"}
                      <button
                        className="text-vermilionHi/70 hover:text-vermilionHi text-xs"
                        onClick={() => void forget(r.mac)}
                      >
                        forget
                      </button>
                    </span>
                  </div>
                ))
              )}
              <Row
                label="Input"
                value={
                  statusQ.isError
                    ? "Pi unreachable"
                    : inputLive
                      ? "live — a remote's keys are landing"
                      : "no remote input heard"
                }
              />
              <button
                className="pill-ghost"
                onClick={() => void statusQ.refetch()}
                disabled={statusQ.isFetching}
              >
                <RefreshCw size={14} className={statusQ.isFetching ? "animate-spin" : undefined} />
                Refresh
              </button>
            </div>
          </section>

          {/* Add a remote */}
          <section className="space-y-2">
            <div className="section-label flex items-center gap-2">
              <Bluetooth size={13} /> Add a remote
            </div>
            <div className="card p-4 space-y-3">
              <p className="text-xs text-parchment leading-relaxed">
                One remote bonds to one Pi. Put the remote in pairing mode —{" "}
                <b>hold OK + Vol− until its LED flashes</b> — then start the scan and pick it
                from the list.
              </p>
              <button className="pill" onClick={() => void scan()} disabled={scanning || !!pairing}>
                {scanning ? <Loader2 size={15} className="animate-spin" /> : <Bluetooth size={15} />}
                {scanning ? "Scanning…" : "Scan for remotes"}
              </button>

              {sortedFound.length > 0 && (
                <div className="space-y-1.5">
                  {sortedFound.map((d) => {
                    const have = remotes.some((r) => r.mac === d.mac);
                    return have ? (
                      <div
                        key={d.mac}
                        className="flex items-center gap-2 font-mono text-xs px-3 py-2 text-parchment/50"
                      >
                        <Check size={12} /> {d.name ?? "?"} · {d.mac} — already paired
                      </div>
                    ) : (
                      <button
                        key={d.mac}
                        className={clsx(
                          "block w-full text-left font-mono text-xs bg-ink/60 border border-hairline rounded px-3 py-2 hover:border-gold transition",
                          remoteish(d.name) ? "text-goldHi" : "text-parchment/60",
                        )}
                        disabled={!!pairing}
                        onClick={() => void pair(d.mac, d.name ?? "remote")}
                      >
                        {pairing === d.mac ? "pairing… " : ""}
                        {d.name ?? "?"} · {d.mac}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <BannerBox banner={banner} />
        </div>
      )}
    </div>
  );
}
